import React, { useEffect, useState } from 'react';
import { supabase, count, sum } from '../../db/engine';
import { useApp } from '../../context/AppContext';
import { getAbrev } from '../../utils/marca';
import {
  DollarSign, FileText, Users, AlertTriangle, TrendingUp,
  Clock, RefreshCw, CheckCircle, XCircle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['var(--sky)', 'var(--steel)', 'var(--sky-light)', 'var(--silver)', '#22C55E'];
const fmt = (n) => `$${Number(n).toLocaleString('es-PA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--steel)', border:'1px solid rgba(var(--sky-rgb),0.4)', borderRadius:8, padding:'10px 14px', fontSize:13 }}>
      <div style={{ fontWeight:700, marginBottom:4 }}>{label}</div>
      {payload.map((p,i) => (
        <div key={i} style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' && p.value > 100 ? fmt(p.value) : p.value}
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user, agenteScope, esAgenteRestringido } = useApp();
  const abrev = getAbrev();
  const [s, setS] = useState({
    totalPolizas: 0, totalClientes: 0, totalAgentes: 0, primaTotal: 0,
    vencen30: 0, vencen60: 0, morosos: 0, comisionesRU: 0,
    reclamosAbiertos: 0, gestiones: 0, porRamo: [], porAgente: []
  });
  const [loading, setLoading] = useState(true);

  const cargar = async () => {
    if (esAgenteRestringido && !agenteScope) return;
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const in30 = new Date(Date.now() + 30*86400000).toISOString().split('T')[0];
      const in60 = new Date(Date.now() + 60*86400000).toISOString().split('T')[0];

      // Alcance del agente (si aplica): solo sus pólizas/clientes
      const restr = esAgenteRestringido;
      const pids  = restr ? (agenteScope?.polizaIds?.length ? agenteScope.polizaIds : [-1]) : null;
      const cids  = restr ? (agenteScope?.clienteIds?.length ? agenteScope.clienteIds : [-1]) : null;
      const scopePol = (q) => restr ? q.in('id', pids) : q;
      const scopePagos = (q) => restr ? q.in('poliza_id', pids) : q;

      // Pólizas activas + prima
      const { count: totalPolizas } = await scopePol(supabase.from('polizas').select('*', { count:'exact', head:true }).eq('estado','activa'));
      const { data: primaRows } = await scopePol(supabase.from('polizas').select('prima').eq('estado','activa'));
      const primaTotal = (primaRows || []).reduce((a,r)=>a+Number(r.prima||0),0);
      // Clientes
      const { count: totalClientes } = restr
        ? { count: (agenteScope?.clienteIds?.length || 0) }
        : await supabase.from('clientes').select('*', { count:'exact', head:true });
      const totalAgentes = restr ? 1 : (await count('agentes', { activo: true })) || 0;
      // Reclamos abiertos
      let rq = supabase.from('reclamos').select('*', { count:'exact', head:true }).in('estado', ['abierto','en_proceso','en_revision']);
      if (restr) rq = rq.in('poliza_id', pids);
      const { count: reclamosAbiertos } = await rq;
      // Gestiones CRM: del agente, sus tareas; si no, todas las abiertas
      let gq = supabase.from('crm_gestiones').select('*', { count:'exact', head:true }).eq('estado','abierto');
      if (restr) gq = gq.eq('asignado_a', user?.id || -1);
      const { count: gestiones } = await gq;

      // Vencimientos y morosos
      const { count: vencen30 } = await scopePol(supabase.from('polizas').select('*', { count:'exact', head:true })
        .gte('fecha_vencimiento', today).lte('fecha_vencimiento', in30).eq('estado','activa'));
      const { count: vencen60 } = await scopePol(supabase.from('polizas').select('*', { count:'exact', head:true })
        .gte('fecha_vencimiento', today).lte('fecha_vencimiento', in60).eq('estado','activa'));
      const { count: morosos } = await scopePagos(supabase.from('pagos').select('*', { count:'exact', head:true })
        .eq('estado','pendiente').lt('fecha_vencimiento', today));

      // Comisión RU pendiente — solo a nivel operativo (no agentes)
      let comisionesRU = 0;
      if (!restr) {
        const { data: comisRU } = await supabase.from('comisiones').select('monto_ru').eq('estado','pendiente');
        comisionesRU = (comisRU || []).reduce((a,r) => a + Number(r.monto_ru || 0), 0);
      }

      // Por ramo (de su cartera si es agente)
      const { data: polRamo } = await scopePol(supabase.from('polizas').select('prima, ramos(nombre)').eq('estado','activa'));
      const ramosMap = {};
      (polRamo || []).forEach(p => {
        const n = p.ramos?.nombre || 'Sin ramo';
        ramosMap[n] = ramosMap[n] || { nombre: n, cantidad: 0, prima: 0 };
        ramosMap[n].cantidad += 1;
        ramosMap[n].prima += Number(p.prima || 0);
      });
      const porRamo = Object.values(ramosMap).sort((a,b) => b.cantidad - a.cantidad).slice(0,6);

      // Por agente — solo vista operativa (no para agentes restringidos)
      let porAgente = [];
      if (!restr) {
        const { data: agentes } = await supabase.from('agentes').select('id, nombre').eq('activo', true);
        porAgente = await Promise.all((agentes || []).map(async a => {
          const polizas = await count('polizas', { agente_id: a.id, estado: 'activa' });
          const { data: cms } = await supabase.from('comisiones').select('monto_ru').eq('agente_id', a.id);
          const comision = (cms || []).reduce((acc, r) => acc + Number(r.monto_ru || 0), 0);
          return { nombre: a.nombre, polizas, comision };
        }));
        porAgente.sort((a,b) => b.polizas - a.polizas);
      }

      setS({
        totalPolizas: totalPolizas || 0,
        totalClientes: totalClientes || 0,
        totalAgentes: totalAgentes || 0,
        primaTotal: primaTotal || 0,
        vencen30: vencen30 || 0,
        vencen60: vencen60 || 0,
        morosos: morosos || 0,
        comisionesRU,
        reclamosAbiertos: reclamosAbiertos || 0,
        gestiones: gestiones || 0,
        porRamo, porAgente
      });
    } catch (err) {
      console.error('Error cargando dashboard:', err);
    }
    setLoading(false);
  };

  useEffect(() => { cargar(); }, [esAgenteRestringido, agenteScope]); // eslint-disable-line

  const kpis = [
    { label:'Pólizas Activas', value:s.totalPolizas, icon:FileText, color:'var(--sky)', sub:`${s.vencen30} vencen en 30 días` },
    { label:'Prima Total Activa', value:fmt(s.primaTotal), icon:DollarSign, color:'var(--text-success)', sub: esAgenteRestringido ? 'mi cartera vigente' : 'cartera vigente' },
    // "Comisiones RU Pend." es interno de RU: no se muestra a agentes
    ...(esAgenteRestringido ? [] : [{ label:`Comisiones ${abrev} Pend.`, value:fmt(s.comisionesRU), icon:TrendingUp, color:'var(--text-warning)', sub:'por confirmar' }]),
    { label: esAgenteRestringido ? 'Mis Clientes' : 'Clientes', value:s.totalClientes, icon:Users, color:'var(--sky-light)', sub: esAgenteRestringido ? 'en mi cartera' : `${s.totalAgentes} agentes activos` },
    { label:'Vencen en 60 días', value:s.vencen60, icon:Clock, color:'var(--text-warning)', sub:'requieren seguimiento' },
    { label:'Pagos Morosos', value:s.morosos, icon:XCircle, color:'var(--text-danger)', sub:'vencidos sin cobrar' },
    { label:'Reclamos Abiertos', value:s.reclamosAbiertos, icon:AlertTriangle, color:'var(--text-danger)', sub:'en proceso' },
    { label:'Gestiones CRM', value:s.gestiones, icon:CheckCircle, color:'var(--silver)', sub:'tareas abiertas' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{esAgenteRestringido ? 'Mi Cartera' : 'Dashboard Ejecutivo'}</div>
          <div className="page-subtitle">
            {new Date().toLocaleDateString('es-PA', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={cargar} disabled={loading}
          style={{ display:'flex', alignItems:'center', gap:6 }}>
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          {loading ? 'Cargando...' : 'Actualizar'}
        </button>
      </div>

      <div className="grid-4" style={{ marginBottom:24 }}>
        {kpis.map((k,i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="stat-card">
              <Icon size={40} className="stat-icon" style={{ color: k.color }} />
              <div className="stat-value" style={{ color: k.color }}>{k.value}</div>
              <div className="stat-label">{k.label}</div>
              <div style={{ fontSize:12, color:'var(--silver)', marginTop:4 }}>{k.sub}</div>
            </div>
          );
        })}
      </div>

      <div className="grid-2" style={{ marginBottom:24 }}>
        <div className="card">
          <div style={{ fontSize:15, fontWeight:700, marginBottom:16 }}>Pólizas por Ramo</div>
          {s.porRamo.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={s.porRamo} margin={{ left:0, right:0 }}>
                <XAxis dataKey="nombre" tick={{ fill:'var(--silver)', fontSize:11 }} />
                <YAxis tick={{ fill:'var(--silver)', fontSize:11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="cantidad" fill="var(--sky)" radius={[4,4,0,0]} name="Pólizas" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>

        <div className="card">
          <div style={{ fontSize:15, fontWeight:700, marginBottom:16 }}>Prima por Ramo</div>
          {s.porRamo.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={s.porRamo} dataKey="prima" nameKey="nombre" cx="50%" cy="50%" outerRadius={80}
                  label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {s.porRamo.map((_,i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>
      </div>

      {!esAgenteRestringido && (
      <div className="card">
        <div style={{ fontSize:15, fontWeight:700, marginBottom:16 }}>Producción por Agente</div>
        {s.porAgente.length > 0 ? (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Agente</th><th>Pólizas Activas</th><th>Comisión {abrev} Generada</th><th>Estado</th></tr></thead>
              <tbody>
                {s.porAgente.map((a,i) => (
                  <tr key={i}>
                    <td style={{ fontWeight:600 }}>{a.nombre}</td>
                    <td><span className="badge badge-info">{a.polizas || 0}</span></td>
                    <td className="text-success">{fmt(a.comision || 0)}</td>
                    <td><span className={`badge ${a.polizas > 0 ? 'badge-success' : 'badge-warning'}`}>{a.polizas > 0 ? 'Activo' : 'Sin producción'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-title">Sin datos aún</div>
            <div>Agrega agentes y pólizas para ver la producción aquí</div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function EmptyChart() {
  return <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--silver)', fontSize:13 }}>Sin datos para mostrar — agrega pólizas para ver estadísticas</div>;
}
